
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Analytics } from "@vercel/analytics/react";

const donateUrl = "https://paypal.me/jacobtchamanian";

function clamp(n, min, max) {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function fmt(n) {
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Stepper({ value, min, max, step = 1, onChange, ariaLabel }) {
  const decDisabled = value <= min;
  const incDisabled = value >= max;

  const btn = (disabled) => ({
    height: 42,
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.18)",
    background: disabled ? "rgba(17,24,39,0.06)" : "#fff",
    fontWeight: 1000,
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 10px 22px rgba(0,0,0,0.10)",
    color: "#0b1220",
    userSelect: "none",
    fontSize: 18,
    lineHeight: "18px",
  });

  const input = {
    width: "100%",
    minWidth: 0,
    padding: "12px 14px",
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.18)",
    fontSize: 17,
    fontWeight: 950,
    color: "#0b1220",
    outline: "none",
    background: "#fff",
    textAlign: "right",
  };

  return (
  <div
    className="sx-stepper"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",           // space between input and buttons
      width: "100%",
    }}
  >
    <input
      className="sx-stepper-input"
      aria-label={ariaLabel}
      type="number"
      value={value}
      onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
      style={{
        width: "50%",        // HALF SIZE INPUT
        padding: "8px 12px",
        borderRadius: "12px",
        border: "1px solid rgba(17,24,39,0.18)",
        fontSize: "16px",
        fontWeight: 900,
        color: "#000",
        background: "#fff",
        minWidth: 0,
        flexShrink: 1        // allows shrinking on phones
      }}
    />

    <div style={{ display: "flex", gap: "6px" }}>  {/* space between − and + */}
      <button
        type="button"
        className="sx-stepper-btn"
        disabled={decDisabled}
        onClick={() => onChange(clamp(value - step, min, max))}
        style={{
          width: 44,
          height: 38,
          borderRadius: 12,
          fontSize: 18,
          fontWeight: 1000,
          background: "#fff",
          color: "#000",
          border: "1px solid rgba(17,24,39,0.22)",
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        −
      </button>

      <button
        type="button"
        className="sx-stepper-btn"
        disabled={incDisabled}
        onClick={() => onChange(clamp(value + step, min, max))}
        style={{
          width: 44,
          height: 38,
          borderRadius: 12,
          fontSize: 18,
          fontWeight: 1000,
          background: "#fff",
          color: "#000",
          border: "1px solid rgba(17,24,39,0.22)",
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        +
      </button>
    </div>
  </div>
);

}

function Row({ label, hint, right }) {
  return (
<div className="sx-row" style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr", gap: 16, alignItems: "center", padding: "12px 0" }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 1100, color: "#0b1220" }}>{label}</div>
        {hint ? <div style={{ fontSize: 13, opacity: 0.72, marginTop: 5, lineHeight: 1.4 }}>{hint}</div> : null}
      </div>
      {right}
    </div>
  );
}

function badgePillBase() {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "linear-gradient(180deg, rgba(124,58,237,0.10), rgba(59,130,246,0.10))",
    color: "#0b1220",
    fontWeight: 1000,
    fontSize: 13,
    lineHeight: 1,
    whiteSpace: "nowrap",
    transformOrigin: "50% 50%",
  };
}

function computeBadges({ miles, activeYears, perWeek, tempo }) {
  const badges = [];

  // Milestones (miles)
  if (miles >= 1) badges.push("🛞 Warm-Up Lap");
  if (miles >= 10) badges.push("🚗 City Cruiser");
  if (miles >= 50) badges.push("🛣️ Highway Hero");
  if (miles >= 100) badges.push("🏁 Century Club");
  if (miles >= 250) badges.push("🏎️ Track Legend");
  if (miles >= 500) badges.push("🌍 Frequent Traveler");
  if (miles >= 1000) badges.push("🚀 Interstellar Mileage");

  // Active years
  if (activeYears >= 5) badges.push("📅 Consistent Driver");
  if (activeYears >= 10) badges.push("🧭 Seasoned Navigator");
  if (activeYears >= 20) badges.push("🧠 Veteran Storyteller");

  // Frequency
  if (perWeek >= 3) badges.push("📈 Steady Pace");
  if (perWeek >= 7) badges.push("🔥 High Frequency Mode");

  // Tempo
  if (tempo === "fast") badges.push("⚡ Turbo Mode");
  if (tempo === "slow") badges.push("🕯️ Cruise Control");

  // Keep it tidy
  return badges.slice(0, 10);
}

export default function App() {
  const tempoMap = useMemo(
    () => ({
      slow: { label: "Slow", v: 100 },
      medium: { label: "Medium", v: 150 },
      fast: { label: "Fast", v: 200 },
    }),
    []
  );

  // Inputs
  const [age, setAge] = useState(40);
  const [firstAge, setFirstAge] = useState(18);
  const [perWeek, setPerWeek] = useState(3);
  const [penisLen, setPenisLen] = useState(6);
  const [minutes, setMinutes] = useState(5);
  const [tempo, setTempo] = useState("medium");

  // Derived values (AUTO)
  const activeYears = Math.max(0, Math.floor(age - firstAge));
  const sessions = activeYears * 52 * perWeek;
  const inches = sessions * (minutes * tempoMap[tempo].v * penisLen);
  const miles = inches / 63360;

  const badges = useMemo(() => computeBadges({ miles, activeYears, perWeek, tempo }), [miles, activeYears, perWeek, tempo]);

  // Badge animation: detect newly unlocked badges
  const prevBadgesRef = useRef(new Set());
  const [popMap, setPopMap] = useState({}); // { badgeText: timestamp }

  useEffect(() => {
    const prev = prevBadgesRef.current;
    const now = new Set(badges);

    const newlyUnlocked = [];
    for (const b of now) {
      if (!prev.has(b)) newlyUnlocked.push(b);
    }

    if (newlyUnlocked.length) {
      const t = Date.now();
      setPopMap((m) => {
        const next = { ...m };
        newlyUnlocked.forEach((b) => (next[b] = t));
        return next;
      });

      // Clear the pop state after animation window
      const timer = setTimeout(() => {
        setPopMap((m) => {
          const next = { ...m };
          newlyUnlocked.forEach((b) => delete next[b]);
          return next;
        });
      }, 1400);

      prevBadgesRef.current = now;
      return () => clearTimeout(timer);
    }

    prevBadgesRef.current = now;
  }, [badges]);

  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 980;

  const shell = {
    minHeight: "100vh",
    padding: 18,
    background:
      "radial-gradient(1200px 800px at 10% 10%, rgba(124,58,237,0.55), transparent 60%), radial-gradient(1200px 800px at 90% 20%, rgba(59,130,246,0.45), transparent 55%), radial-gradient(1200px 800px at 40% 95%, rgba(236,72,153,0.25), transparent 60%), linear-gradient(180deg, #070a14, #0b1220 60%, #070a14)",
    fontFamily:
      "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, Apple Color Emoji, Segoe UI Emoji",
  };

  const card = {
    borderRadius: 24,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(17,24,39,0.10)",
    boxShadow: "0 22px 60px rgba(0,0,0,0.14)",
    padding: 20,
  };

  const darkCard = {
    borderRadius: 24,
    background: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.16)",
    padding: 18,
  };

  const donateBtn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid rgba(17,24,39,0.18)",
    background: "#ffffff",
    color: "#0b1220",
    fontWeight: 1100,
    textDecoration: "none",
    boxShadow: "0 16px 34px rgba(0,0,0,0.14)",
    width: "100%",
    gap: 8,
    fontSize: 16,
  };

  const heroPill = {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.16)",
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
    fontSize: 13,
  };

  const isPopping = (badgeText) => Object.prototype.hasOwnProperty.call(popMap, badgeText);

  return (
    <div style={shell}>
      {/* Badge keyframes */}
      <style>{`
        @keyframes badgePop {
          0%   { transform: scale(0.92); filter: drop-shadow(0 0 0 rgba(124,58,237,0)); }
          45%  { transform: scale(1.10); filter: drop-shadow(0 10px 24px rgba(124,58,237,0.35)); }
          70%  { transform: scale(0.98); }
          100% { transform: scale(1.00); filter: drop-shadow(0 0 0 rgba(124,58,237,0)); }
        }
        @keyframes badgeShine {
          0%   { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
            @media (max-width: 420px) {
  .App-header, .sx-container {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .sx-card {
    padding: 14px !important;
    border-radius: 18px !important;
  }

  .sx-stepper {
    grid-template-columns: minmax(0, 1fr) 36px 36px !important;
    gap: 6px !important;
  }
}
        
   @media (max-width: 520px) {
          .sx-row {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }

          /* make the whole stepper smaller */
          .sx-stepper {
            grid-template-columns: minmax(0, 1fr) 38px 38px !important;
            gap: 6px !important;
          }

          .sx-stepper-input {
            padding: 8px 10px !important;
            font-size: 14px !important;
            border-radius: 12px !important;
            min-width: 0 !important;
          }

          .sx-stepper-btn {
            width: 38px !important;
            height: 34px !important;
            border-radius: 12px !important;
            font-size: 18px !important;
            line-height: 18px !important;
            padding: 0 !important;
          }
        }


      `}</style>

      <div style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gap: 14 }}>
        {/* Website hero */}
        <div style={{ color: "#fff", display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span style={heroPill}>No tracking</span>
            <span style={heroPill}>No saving</span>
            <span style={heroPill}>Runs locally</span>
            <span style={heroPill}>Just for fun</span>
          </div>

          <div style={{ fontSize: isDesktop ? 46 : 34, fontWeight: 1150, lineHeight: 1.05 }}>
            Sexodometer<span style={{ opacity: 0.9 }}>™</span>
          </div>

          <div style={{ fontSize: 17, opacity: 0.92, lineHeight: 1.7, maxWidth: 920 }}>
            Sexodometer turns your personal timeline into symbolic “mileage.” It’s not crude, not judgmental, and not a gimmick —
            it’s as real as the honesty of your inputs. I created it as a conversation opener: a fun, classy way to break the ice and
            get comfortable talking about topics that usually take hours (or several dates) to reach.
          </div>

          <div style={{ fontSize: 14, opacity: 0.9 }}>
            Promise: No tracking. No saving. No selling. Everything runs locally in your browser.
          </div>
        </div>

        {/* Main layout */}
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1.2fr 0.9fr" : "1fr", gap: 14, alignItems: "start" }}>
          {/* Inputs */}
          <div style={card}>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 1150, color: "#0b1220" }}>Enter your inputs</div>
              <div style={{ fontSize: 13, opacity: 0.72, marginTop: -4 }}>
                Tip: use the + / − buttons to hit exact numbers. Results update instantly.
              </div>

              <div style={{ borderTop: "1px solid rgba(17,24,39,0.08)", marginTop: 6 }} />

              <Row label="Current age" hint="Your age today." right={<Stepper value={age} min={18} max={100} step={1} onChange={setAge} ariaLabel="Current age" />} />

              <Row
                label="Age of first intercourse"
                hint="When your odometer started."
                right={<Stepper value={firstAge} min={10} max={Math.max(10, age - 1)} step={1} onChange={setFirstAge} ariaLabel="Age of first intercourse" />}
              />

              <Row label="Average per week" hint="Example consensus: 3/week." right={<Stepper value={perWeek} min={0} max={30} step={1} onChange={setPerWeek} ariaLabel="Average per week" />} />

              <Row
                label="Average length (inches)"
                hint='Example: 6".'
                right={<Stepper value={penisLen} min={1} max={12} step={0.5} onChange={setPenisLen} ariaLabel="Length in inches" />}
              />

              <Row label="Minutes per session" hint="Example: 5 minutes." right={<Stepper value={minutes} min={1} max={60} step={1} onChange={setMinutes} ariaLabel="Minutes per session" />} />

              <div className="sx-row" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 1fr", gap: 16, padding: "12px 0" }}>
  <div style={{ fontSize: 18, fontWeight: 1100 }}>Tempo</div>
  <div style={{ width: "100%" }}>
    <select
      value={tempo}
      onChange={(e) => setTempo(e.target.value)}
      style={{
        width: "100%",
        padding: "14px 16px",
        borderRadius: 16,
        border: "1px solid rgba(17,24,39,0.22)",
        fontSize: 18,
        fontWeight: 900,
        color: "#000",
        background: "#fff",
      }}
    >
      <option value="slow">Slow</option>
      <option value="medium">Medium</option>
      <option value="fast">Fast</option>
    </select>
  </div>
</div>


              <div style={{ borderTop: "1px solid rgba(17,24,39,0.08)", marginTop: 6 }} />

              {/* Donate Section */}
<div style={{ marginTop: 24, padding: 18, borderRadius: 20, background: "rgba(0,0,0,0.45)", color: "#000" }}>
  <div style={{ fontSize: 20, fontWeight: 1100, marginBottom: 8 }}>Donate</div>
  <div style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 14, opacity: 0.85 }}>
    If this made you smile or helped break the ice, you can donate <b>$1</b> as appreciation for the idea.
    Totally optional. No data collected. No strings attached.
  </div>
  <a
    href={donateUrl}
    target="_blank"
    rel="noreferrer"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "14px 20px",
      borderRadius: 14,
      background: "linear-gradient(90deg, #ec4899, #7c3aed)",
      fontSize: 20,
      fontWeight: 1100,
      color: "#fff",
      textDecoration: "none",
      boxShadow: "0 14px 34px rgba(236,72,153,0.40)",
      width: "100%",
      transition: "transform 0.15s ease, opacity 0.15s ease"
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
    onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
    onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    Donate $1 ☕
  </a>
</div>

              <div style={{ fontSize: 13, opacity: 0.72, marginTop: 2, lineHeight: 1.5 }}>
                For entertainment only. Results are estimates based on your inputs.
              </div>

              <div style={{ fontSize: 13, opacity: 0.78, marginTop: 10 }}>
                Created by <b>Jacob (Hagop) Tchamanian</b>
              </div>
            </div>
          </div>

          {/* Results + Badges */}
          <div style={{ display: "grid", gap: 14 }}>
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 1000, color: "#0b1220", opacity: 0.8 }}>Total Sexual Mileage</div>
              <div style={{ marginTop: 10, fontSize: 40, fontWeight: 1200, color: "#0b1220" }}>{fmt(miles)} miles</div>

              <div style={{ marginTop: 12, display: "grid", gap: 8, fontSize: 14, color: "#0b1220", opacity: 0.9 }}>
                <div>
                  Active years: <b>{activeYears}</b>
                </div>
                <div>
                  Total sessions: <b>{Math.round(sessions).toLocaleString()}</b>
                </div>
                <div>
                  Tempo: <b>{tempoMap[tempo].v}</b> in/out per minute
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 1150, color: "#0b1220" }}>Badges Unlocked</div>
                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {badges.length ? (
                    badges.map((b) => {
                      const popping = isPopping(b);
                      const style = {
                        ...badgePillBase(),
                        animation: popping ? "badgePop 720ms cubic-bezier(.2,.9,.2,1) both" : "none",
                        boxShadow: popping ? "0 18px 40px rgba(124,58,237,0.22)" : "none",
                        borderColor: popping ? "rgba(124,58,237,0.25)" : "rgba(17,24,39,0.10)",
                        background: popping
                          ? "linear-gradient(90deg, rgba(124,58,237,0.16), rgba(59,130,246,0.16), rgba(236,72,153,0.12))"
                          : "linear-gradient(180deg, rgba(124,58,237,0.10), rgba(59,130,246,0.10))",
                        backgroundSize: popping ? "220% 220%" : "auto",
                        animationName: popping ? "badgePop, badgeShine" : undefined,
                        animationDuration: popping ? "720ms, 900ms" : undefined,
                        animationTimingFunction: popping ? "cubic-bezier(.2,.9,.2,1), linear" : undefined,
                        animationIterationCount: popping ? "1, 1" : undefined,
                        animationFillMode: popping ? "both, both" : undefined,
                      };
                      return (
                        <span key={b} style={style}>
                          {b}
                        </span>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: 13, opacity: 0.7 }}>
                      Start sliding—your dashboard will unlock badges automatically.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={card}>
              <div style={{ fontSize: 16, fontWeight: 1150, color: "#0b1220" }}>How it’s calculated</div>
              <div style={{ marginTop: 12, fontSize: 14, color: "#0b1220", opacity: 0.9, lineHeight: 1.7 }}>
                <div>
                  Active years = <b>Age − First time</b>
                </div>
                <div>
                  Sessions = <b>Active years × 52 × Avg/week</b>
                </div>
                <div>
                  Inches = <b>Sessions × (Minutes × Tempo × Length)</b>
                </div>
                <div>
                  Miles = <b>Inches ÷ 63,360</b>
                </div>
              </div>
            </div>

            <div style={darkCard}>
              <div style={{ color: "#fff", fontWeight: 1100, fontSize: 14 }}>Privacy note</div>
              <div style={{ color: "rgba(255,255,255,0.88)", marginTop: 10, fontSize: 14, lineHeight: 1.7 }}>
                No login. No stored inputs. No tracking cookies. The calculator runs locally in your browser.
              </div>
            </div>
          </div>
        </div>

        <Analytics />
      </div>
    </div>
  );
}
